import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";

import { api } from "../lib/api";
import type { paths } from "../generated/api-schema";
import { requireAuth } from "../lib/requireAuth";
import { Pagination, PageMeta } from "../components/Pagination";

const PAGE_SIZE = 20;

// `page` is the URL-visible 1-based page number. Bookmarkable + survives
// refresh. validateSearch clamps it to a positive integer so a typo in
// the URL doesn't throw.
type HistorySearch = {
  page?: number | undefined;
};

export const Route = createFileRoute("/history")({
  beforeLoad: ({ context, location }) =>
    requireAuth(context.queryClient, location.href),
  validateSearch: (raw: Record<string, unknown>): HistorySearch => {
    const p = Number(raw.page);
    const page = Number.isFinite(p) && p >= 1 ? Math.floor(p) : undefined;
    return page !== undefined ? { page } : {};
  },
  component: HistoryPage,
});

// Pulled from the regenerated openapi-fetch schema — keeps the
// types in lockstep with the backend's AttemptHistoryPageDTO.
type AttemptHistoryPage =
  paths["/attempts"]["get"]["responses"]["200"]["content"]["application/json"];
type AttemptHistoryItem = AttemptHistoryPage["items"][number];

function HistoryPage() {
  const { page: pageSearch } = Route.useSearch();
  const navigate = useNavigate();
  const page = pageSearch ?? 1;
  const offset = (page - 1) * PAGE_SIZE;

  const q = useQuery({
    queryKey: ["attempt-history", page],
    queryFn: async (): Promise<AttemptHistoryPage> => {
      const { data, error } = await api.GET("/attempts", {
        params: { query: { limit: PAGE_SIZE, offset } },
      });
      if (error) throw error;
      return data;
    },
    // Keep the previous page rendered while the next page fetches so
    // clicking Next doesn't flash the loading skeleton. Same pattern
    // /browse already uses (keepPreviousData).
    placeholderData: keepPreviousData,
  });

  const total = q.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showFrom = total === 0 ? 0 : offset + 1;
  const showTo = Math.min(offset + PAGE_SIZE, total);

  const goToPage = (next: number) => {
    const clamped = Math.max(1, Math.min(totalPages, next));
    navigate({
      to: "/history",
      // page=1 is the implicit default; omit it from the URL.
      search: clamped === 1 ? {} : { page: clamped },
    });
  };

  return (
    <section className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12">
      <div className="eyebrow mb-3">Practice history</div>
      <h1 className="font-serif text-3xl sm:text-4xl text-ink-900 mb-3 tracking-tight">
        Recent attempts
      </h1>
      <p className="text-ink-500 mb-6 max-w-prose">
        Every exercise you've answered, newest first. Click any row to re-open
        the exercise.
      </p>

      {q.isLoading && <Loading />}
      {q.isError && <ErrorState />}
      {q.data && q.data.items.length === 0 && page === 1 && <Empty />}
      {q.data && q.data.items.length > 0 && (
        <>
          <PageMeta from={showFrom} to={showTo} total={total} />
          <Pagination
            page={page}
            totalPages={totalPages}
            onPrev={() => goToPage(page - 1)}
            onNext={() => goToPage(page + 1)}
            isFetching={q.isFetching}
            ariaLabel="History pagination (top)"
            labels={{ prev: "← Newer", next: "Older →" }}
            className="mb-3"
          />
          <ol className="divide-y divide-ink-100 border-y border-ink-100">
            {q.data.items.map((a) => (
              <AttemptRow key={a.id} attempt={a} />
            ))}
          </ol>
          <Pagination
            page={page}
            totalPages={totalPages}
            onPrev={() => goToPage(page - 1)}
            onNext={() => goToPage(page + 1)}
            isFetching={q.isFetching}
            ariaLabel="History pagination"
            labels={{ prev: "← Newer", next: "Older →" }}
          />
        </>
      )}
    </section>
  );
}

function AttemptRow({ attempt }: { attempt: AttemptHistoryItem }) {
  const passed = attempt.correct === true;
  const failed = attempt.correct === false;
  const indeterminate = attempt.correct === null;

  const verdict = passed ? "✓" : failed ? "✗" : "·";
  const verdictTone = passed
    ? "text-emerald-700"
    : failed
      ? "text-rose-700"
      : "text-ink-400";

  const when = new Date(attempt.startedAt);
  const dateStr = when.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  const timeStr = when.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });

  const tookSec =
    attempt.responseTimeMs != null
      ? `${(attempt.responseTimeMs / 1000).toFixed(1)}s`
      : null;

  return (
    <li className="py-4">
      <Link
        to="/progress/exercise/$exerciseId"
        params={{ exerciseId: String(attempt.exerciseId) }}
        className="block group hover:bg-ink-50/60 -mx-2 px-2 py-1 rounded-sm transition-colors"
        title="See the full history of attempts on this exercise"
      >
        <div className="flex items-start gap-3">
          <span
            className={`font-serif text-xl tabular-nums w-5 text-center ${verdictTone}`}
            aria-label={
              passed ? "passed" : failed ? "failed" : "no grade recorded"
            }
          >
            {verdict}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs text-ink-400">
              <time dateTime={attempt.startedAt}>
                {dateStr} · {timeStr}
              </time>
              <span>{attempt.exerciseType}</span>
              {tookSec && <span>{tookSec}</span>}
              {attempt.scaffoldingLevel >= 0 && (
                <span>
                  scaffold {attempt.scaffoldingLevel}
                  {attempt.scaffoldingLevel === 0 && " · cold"}
                </span>
              )}
            </div>
            <div className="mt-1 text-ink-900 group-hover:text-ink-700 transition-colors">
              {attempt.exercisePrompt}
            </div>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-2 text-sm">
              <span className="text-ink-500">your answer:</span>
              <span className={passed ? "text-ink-900" : "text-ink-700"}>
                {attempt.response ?? <em className="text-ink-400">(empty)</em>}
              </span>
              {failed && (
                <>
                  <span className="text-ink-500">· expected:</span>
                  <span className="text-ink-900">
                    {attempt.exerciseExpected}
                  </span>
                </>
              )}
            </div>
            {(attempt.skills ?? []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(attempt.skills ?? []).map((s) => (
                  <span
                    key={s.skillCode}
                    className={`text-xs px-1.5 py-0.5 border ${
                      s.passed
                        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                        : "border-rose-200 bg-rose-50 text-rose-800"
                    }`}
                  >
                    {s.skillName}
                  </span>
                ))}
              </div>
            )}
            {indeterminate && (
              <div className="mt-1 text-xs text-ink-400">
                Submitted but not graded (incomplete or aborted).
              </div>
            )}
          </div>
        </div>
      </Link>
    </li>
  );
}

function Loading() {
  return (
    <div className="text-ink-500 py-12 text-center">
      <div className="hairline mx-auto mb-4 w-24" />
      Loading your attempts…
    </div>
  );
}

function ErrorState() {
  return (
    <div className="text-ink-500 py-12 text-center">
      <p className="mb-3">Couldn't load your attempt history.</p>
      <p className="text-sm">
        Try refreshing, or{" "}
        <Link to="/home" className="underline underline-offset-4">
          go back to the dashboard
        </Link>
        .
      </p>
    </div>
  );
}

function Empty() {
  return (
    <div className="text-ink-500 py-12 text-center">
      <p className="mb-3">No attempts yet.</p>
      <Link
        to="/practice"
        className="inline-block bg-accent text-accent-fg px-4 py-2 hover:bg-accent-hover transition-colors"
      >
        Start practicing →
      </Link>
    </div>
  );
}
